import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import {
  Activity,
  BarChart3,
  Cloud,
  Database,
  Droplets,
  Eye,
  EyeOff,
  Gauge,
  Shield,
  TrendingUp,
  Waves,
  Zap,
  Home,
} from "lucide-react";
import { toast } from "react-toastify";
import { post } from "../../Libraries/Ajax/HttpService";
import zxcvbn from "zxcvbn";
import { Progress } from "antd";
import ReactGA from "react-ga4";
import { useDistrict } from "../../utils/DistrictContext";
import LogoImage from "../../assets/images/mizuwatch-logo-white.png";
import { motion } from "framer-motion";

export function Login() {
  const navigate = useNavigate();
  const { setSelectedDistrict } = useDistrict();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [reset, setReset] = useState(false);
  const [otp, setOtp] = useState(false);
  const [otpCode, setOtpCode] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [score, setScore] = useState(0);
  const [confirmFeedback, setConfirmFeedback] = useState("");

  const colors = {
    background: "#050b16",
    primary: "#00b8f1",
    secondary: "#0060f1",
    cardBg: "#0f1f30",
    border: "#2a2520",
    text: "#ffffff",
    textSecondary: "#BDB9B5",
    gradient: "linear-gradient(135deg, #00b8f1, #0060f1)",
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const MCData = localStorage.getItem("MCData");
    if (MCData) {
      const parsed = JSON.parse(MCData);
      if (parsed?.token) navigate("/dashboard", { replace: true });
    }
    return () => (document.body.style.overflow = "auto");
  }, []);

  const getScore = (pwd) => {
    if (pwd.length < 8) return 0;
    if (!/[A-Z]/.test(pwd) || !/[0-9]/.test(pwd) || !/[!@#$%^&*]/.test(pwd))
      return 1;
    if (pwd.length > 21) return 4;
    if (pwd.length > 12) return 3;
    return 2;
  };

  const handleNewPassword = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    setScore(getScore(val));
  };

  const handleConfirm = (e) => {
    const v = e.target.value;
    setConfirmPassword(v);
    setConfirmFeedback(v !== newPassword ? "Password mismatch" : "");
  };

  const sendResetOtp = (e) => {
    e.preventDefault();
    post(`apis/forgot_password?email=${email}`)
      .then(() => {
        toast.success("OTP sent");
        setReset(false);
        setOtp(true);
      })
      .catch((err) => toast.error(err?.response?.data?.msg || "Error"));
  };

  const submitOtpReset = () => {
    if (newPassword !== confirmPassword || score < 2) {
      toast.error("Password too weak / mismatch");
      return;
    }

    post("apis/otp_confirm_password", {
      email,
      otp: otpCode,
      new_password: newPassword,
      confirm_password: confirmPassword,
    })
      .then(() => {
        toast.success("Password reset done");
        setOtp(false);
        setReset(false);
      })
      .catch((err) => toast.error(err?.response?.data?.msg || "Error"));
  };

  const handleLogin = (e) => {
    e.preventDefault();
    post("apis/user_login", { username: email, password })
      .then((res) => {
        toast.success("Logged In");
        const d = res.data;
        const districts = d.user_districts || [];
        const defaultDistrict = districts[0]?.district_name || "";
        setSelectedDistrict(defaultDistrict);

        const uid = email.replace("@", "*");
        ReactGA.set({ user_id: uid });
        ReactGA.event({ category: "User", action: "Login", label: uid });

        localStorage.setItem(
          "MCData",
          JSON.stringify({
            userName: email,
            token: d.access_token,
            userid: d.user_id,
            name: d.first_name,
            type: d.user_type,
            district: districts,
            selectedDistrict: defaultDistrict,
            permission: d.permission || [],
            graphType: d.graph,
          }),
        );

        navigate("/dashboard", { replace: true });
      })
      .catch(() => toast.error("Invalid Login"));
  };
  const [isTypingPassword, setIsTypingPassword] = useState(false);

  return (
    <div
      className="h-screen w-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundColor: colors.background,
        fontFamily: "Plus Jakarta Sans, sans-serif",
      }}
    >
      {/* --- NEW HOME BUTTON --- */}
      <motion.a
        href="https://www.mizuwatch.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-8 left-8 z-50 flex items-center gap-2 px-4 py-2 rounded-full border transition-all"
        style={{
          backgroundColor: `${colors.cardBg}99`,
          borderColor: `${colors.primary}40`,
          backdropFilter: "blur(8px)",
        }}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{
          scale: 1.05,
          borderColor: colors.primary,
          boxShadow: `0 0 20px ${colors.primary}40`,
        }}
      >
        <Home className="w-5 h-5" style={{ color: colors.primary }} />
        <span
          className="text-sm font-medium tracking-wide"
          style={{ color: colors.text }}
        >
          Home
        </span>
      </motion.a>
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[10%] left-[15%] w-[500px] h-[500px] rounded-full blur-3xl animate-float-slow"
          style={{
            backgroundImage: `linear-gradient(135deg, ${colors.primary}33, ${colors.secondary}1A)`,
          }}
        />
        <div
          className="absolute top-[60%] right-[10%] w-[600px] h-[600px] rounded-full blur-3xl animate-float-reverse"
          style={{
            backgroundImage: `linear-gradient(45deg, ${colors.secondary}26, ${colors.primary}0D)`,
          }}
        />
        <div
          className="absolute bottom-[20%] left-[40%] w-[400px] h-[400px] rounded-full blur-3xl animate-pulse-slow"
          style={{
            backgroundImage: `linear-gradient(135deg, ${colors.primary}1A, transparent)`,
          }}
        />
        <div
          className="absolute top-[30%] right-[30%] w-[350px] h-[350px] rounded-full blur-3xl animate-float-slow"
          style={{
            backgroundImage: `linear-gradient(225deg, ${colors.secondary}33, transparent)`,
            animationDelay: "2s",
          }}
        />
      </div>

      <div className="relative z-10 w-full h-screen flex">
        {/* ---------------- LEFT SIDE - Branding ---------------- */}
        {/* ---------------- Floating SVGs ---------------- */}
        <div className="absolute inset-0 pointer-events-none">
          <Droplets
            className="absolute top-25 left-12 w-12 h-12 opacity-10"
            style={{ color: colors.primary }}
          />
          <Waves
            className="absolute top-48 left-32 w-12 h-12 opacity-8"
            style={{ color: colors.secondary }}
          />
          <Gauge
            className="absolute top-25 right-252 w-14 h-14 opacity-12"
            style={{ color: colors.primary }}
          />
          <Activity
            className="absolute top-56 right-260 w-10 h-10 opacity-12"
            style={{ color: colors.secondary }}
          />
          <Database
            className="absolute bottom-36 left-16 w-12 h-12 opacity-10"
            style={{ color: colors.primary }}
          />
          <TrendingUp
            className="absolute bottom-20 left-32 w-11 h-11 opacity-14"
            style={{ color: colors.secondary }}
          />
          <BarChart3
            className="absolute bottom-32 right-260 w-13 h-13 opacity-12"
            style={{ color: colors.primary }}
          />
          <Zap
            className="absolute bottom-52 right-254 w-10 h-10 opacity-12"
            style={{ color: colors.secondary }}
          />
          <Cloud
            className="absolute top-1/2 left-6 w-11 h-11 opacity-5"
            style={{ color: colors.primary }}
          />
          <Shield
            className="absolute top-1/2 right-252 w-10 h-10 opacity-12"
            style={{ color: colors.secondary }}
          />
        </div>
        <div
          className="flex-1 flex flex-col items-center justify-center p-12 animate-fadeIn max-[992px]:hidden"
          style={{ backgroundColor: "rgb(10, 22, 40)" }}
        >
          {/* <img
  src={LogoImage}
  alt="Mizu Watch Logo"
  className="w-72 h-72 object-contain drop-shadow-lg"
  style={{
    filter: `drop-shadow(0 50px 100px rgba(0, 170, 255, 0.9)) drop-shadow(0 30px 70px rgba(0, 0, 0, 0.5))`,
  }}
/>

    <h1 className="text-6xl font-bold tracking-wider text-gradient-animated mt-6">
  MIZU WATCH
</h1> */}

          <div className="w-full max-w-lg flex flex-col items-center relative z-10">
            <img
              src={LogoImage}
              alt="Mizu Watch Logo"
              className="w-120 h-120 object-contain drop-shadow-lg"
            
            />

            <div className="text-center">
             
              {/* <div className="relative inline-block">
                <motion.h1
                  className="text-6xl font-bold tracking-wider relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.8,
                    ease: "easeOut",
                  }}
                >
                  <motion.span
                    className="text-transparent bg-clip-text"
                    style={{
                      backgroundImage: `linear-gradient(270deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`,
                      backgroundSize: "200% auto",
                    }}
                    animate={{
                      backgroundPosition: ["200% center", "0% center"],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "linear",
                    }}
                  >
                    MIZU WATCH
                  </motion.span>

                  <motion.span
                    className="absolute inset-0 text-6xl font-bold tracking-wider"
                    style={{
                      color: "transparent",
                      WebkitTextStroke: `1px ${colors.primary}30`,
                    }}
                    animate={{
                      textShadow: [
                        `0 0 10px ${colors.primary}80, 0 0 20px ${colors.primary}60, 0 0 30px ${colors.primary}40`,
                        `0 0 20px ${colors.secondary}80, 0 0 40px ${colors.secondary}60, 0 0 60px ${colors.secondary}40`,
                        `0 0 10px ${colors.primary}80, 0 0 20px ${colors.primary}60, 0 0 30px ${colors.primary}40`,
                      ],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    MIZU WATCH
                  </motion.span>
                </motion.h1>

             
                <div
                  className="absolute -inset-4 rounded-lg blur-2xl -z-10 opacity-30"
                  style={{
                    background: colors.gradient,
                  }}
                />
              </div> */}

              {/* <motion.p
                className="text-lg"
                style={{ color: colors.textSecondary }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Empowering decisions through data-driven water intelligence
              </motion.p> */}
            </div>
          </div>
        </div>

        {/* ---------------- RIGHT SIDE - Login Form ---------------- */}
        <div
          className="flex-1 flex flex-col items-center justify-center p-12 animate-scaleIn backdrop-blur-md overflow-auto"
          style={{
            backgroundColor: `${colors.cardBg}66`,
            borderLeft: `2px solid rgb(15, 31, 48)`,
          }}
        >
          <div className="w-full max-w-md">
            <div
              className="rounded-2xl shadow-2xl p-10 border backdrop-blur-xl hover:shadow-[#00b8f1]/20 transition-all duration-500 min-h-[600px] flex flex-col justify-center"
              style={{
                backgroundColor: `${colors.cardBg}CC`,
                borderColor: colors.border,
              }}
            >
              {/* ------ Your login, reset and otp forms start here ------ */}

              {!reset && !otp && (
                <>
                  <div className="text-start mb-8">
                    <h2
                      className="text-3xl font-semibold text-transparent bg-clip-text mb-3"
                      style={{ backgroundImage: colors.gradient }}
                    >
                      Welcome Back
                    </h2>
                    <p
                      className="text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      Sign in to access your dashboard
                    </p>
                  </div>

                  <form onSubmit={handleLogin} className="space-y-6">
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: colors.textSecondary }}
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3.5 rounded-lg border focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                        style={{
                          backgroundColor: `${colors.background}CC`,
                          borderColor: colors.border,
                          color: colors.text,
                        }}
                        placeholder="Enter your email"
                        required
                      />
                    </div>

                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: colors.textSecondary }}
                      >
                        Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-4 py-3.5 rounded-lg border pr-12 focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          style={{
                            backgroundColor: `${colors.background}CC`,
                            borderColor: colors.border,
                            color: colors.text,
                          }}
                          placeholder="Enter your password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                          style={{ color: colors.textSecondary }}
                        >
                          {showPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <div className="text-right mt-2">
                        <button
                          type="button"
                          onClick={() => setReset(true)}
                          className="text-sm transition-colors"
                          style={{ color: colors.primary }}
                        >
                          Forgot Password?
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full py-4 rounded-lg font-semibold text-white transition-all hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98]"
                      style={{ backgroundImage: colors.gradient }}
                    >
                      Sign In
                    </button>
                  </form>
                </>
              )}

              {reset && !otp && (
                <form onSubmit={sendResetOtp} className="space-y-6">
                  <h2
                    className="text-2xl text-center text-transparent bg-clip-text"
                    style={{ backgroundImage: colors.gradient }}
                  >
                    Reset Password
                  </h2>
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.textSecondary }}
                    >
                      Email ID
                    </label>
                    <input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3.5 rounded-lg border 
             bg-[rgba(5,11,22,0.8)] 
             border-[rgb(42,37,32)] 
             focus:border-[#00b8f1] 
             outline-none"
                      style={{ color: colors.textSecondary }}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <button
                    className="w-full py-4 rounded-lg text-white"
                    style={{ backgroundImage: colors.gradient }}
                  >
                    Send OTP
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReset(false);
                      setOtp(false);
                    }}
                    className="text-sm mb-2 transition-colors"
                    style={{ color: colors.primary }}
                  >
                    ← Back
                  </button>
                </form>
              )}

              {/* {otp && (
                <div className="space-y-4">
                  <h2 className="text-2xl text-center text-transparent bg-clip-text"
                    style={{ backgroundImage: colors.gradient }}>
                    Set New Password
                  </h2>

                  <input placeholder="New Password" value={newPassword} onChange={handleNewPassword}
                    className="w-full px-4 py-3.5 rounded-lg border 
             bg-[rgba(5,11,22,0.8)] 
             border-[rgb(42,37,32)] 
             focus:border-[#00b8f1] 
             outline-none"
                    style={{ color: colors.textSecondary }} />
                  <Progress percent={(score + 1) * 20} showInfo={false} />

                  <input placeholder="Confirm Password" onChange={handleConfirm}
                    className="w-full px-4 py-3.5 rounded-lg border 
             bg-[rgba(5,11,22,0.8)] 
             border-[rgb(42,37,32)] 
             focus:border-[#00b8f1] 
             outline-none"
                    style={{ color: colors.textSecondary }} />
                  {confirmFeedback && <p className="text-red-500 text-sm">{confirmFeedback}</p>}

                  <input placeholder="OTP Code" value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-lg border 
             bg-[rgba(5,11,22,0.8)] 
             border-[rgb(42,37,32)] 
             focus:border-[#00b8f1] 
             outline-none"
                    style={{ color: colors.textSecondary }} />

                  <button onClick={submitOtpReset}
                    className="w-full py-4 rounded-lg text-white"
                    style={{ backgroundImage: colors.gradient }}>
                    Submit
                  </button>
                </div>
              )} */}
              {otp && (
                <div className="space-y-4">
                  <h2
                    className="text-2xl text-center text-transparent bg-clip-text"
                    style={{ backgroundImage: colors.gradient }}
                  >
                    Set New Password
                  </h2>

                  {/* New Password */}
                  <input
                    type="password"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewPassword(value);
                      setIsTypingPassword(value.length > 0);
                      handleNewPassword(e);
                    }}
                    className="w-full px-4 py-3.5 rounded-lg border 
        bg-[rgba(5,11,22,0.8)] 
        border-[rgb(42,37,32)] 
        focus:border-[#00b8f1] 
        outline-none"
                    style={{ color: colors.textSecondary }}
                  />

                  {/* Show only when typing */}
                  {isTypingPassword && (
                    <>
                      {/* Thin Strength Bar */}
                      <div>
                        <div className="h-1.5 w-full bg-[#1a2a3a] rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              score <= 1
                                ? "bg-red-500 w-[20%]"
                                : score === 2
                                  ? "bg-orange-400 w-[40%]"
                                  : score === 3
                                    ? "bg-yellow-400 w-[60%]"
                                    : score === 4
                                      ? "bg-blue-400 w-[80%]"
                                      : "bg-green-500 w-full"
                            }`}
                          />
                        </div>

                        <p className="text-sm mt-2 text-gray-300">
                          Strength :
                          <span
                            className={`ml-1 font-medium ${
                              score <= 1
                                ? "text-red-500"
                                : score === 2
                                  ? "text-orange-400"
                                  : score === 3
                                    ? "text-yellow-400"
                                    : score === 4
                                      ? "text-blue-400"
                                      : "text-green-500"
                            }`}
                          >
                            {score <= 1
                              ? "Very Weak"
                              : score === 2
                                ? "Weak"
                                : score === 3
                                  ? "Medium"
                                  : score === 4
                                    ? "Strong"
                                    : "Very Strong"}
                          </span>
                        </p>
                      </div>

                      {/* Password Requirements */}
                      <div className="mt-2 text-sm space-y-1">
                        <p className="text-red-400 font-medium">
                          Password Requirements:
                        </p>

                        <p
                          className={
                            newPassword.length >= 8
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          Minimum Length: 8 characters
                        </p>

                        <p
                          className={
                            /[A-Z]/.test(newPassword)
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          One uppercase letter (A-Z)
                        </p>

                        <p
                          className={
                            /[0-9]/.test(newPassword)
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          One number (0-9)
                        </p>

                        <p
                          className={
                            /[!@#$%^&*]/.test(newPassword)
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          One special character
                        </p>

                        <p
                          className={
                            !/\s/.test(newPassword)
                              ? "text-green-400"
                              : "text-red-400"
                          }
                        >
                          No spaces
                        </p>
                      </div>
                    </>
                  )}

                  {/* Confirm Password */}
                  <input
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={handleConfirm}
                    className="w-full px-4 py-3.5 rounded-lg border 
        bg-[rgba(5,11,22,0.8)] 
        border-[rgb(42,37,32)] 
        focus:border-[#00b8f1] 
        outline-none"
                    style={{ color: colors.textSecondary }}
                  />

                  {confirmFeedback && (
                    <p
                      className={`text-sm ${
                        confirmFeedback === "Password matched"
                          ? "text-green-400"
                          : "text-red-500"
                      }`}
                    >
                      {confirmFeedback}
                    </p>
                  )}

                  {/* OTP */}
                  <input
                    placeholder="OTP Code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-lg border 
        bg-[rgba(5,11,22,0.8)] 
        border-[rgb(42,37,32)] 
        focus:border-[#00b8f1] 
        outline-none"
                    style={{ color: colors.textSecondary }}
                  />

                  <button
                    onClick={submitOtpReset}
                    className="w-full py-4 rounded-lg text-white"
                    style={{ backgroundImage: colors.gradient }}
                  >
                    Submit
                  </button>
                </div>
              )}
              {/* ----------- */}
            </div>
          </div>
          <div className="absolute bottom-6 left-0 right-0 z-10 text-center space-y-2">
            <div
              className="flex items-center justify-center gap-3 text-xs"
              style={{ color: colors.textSecondary }}
            >
              <a href="#" style={{ color: colors.textSecondary }}>
                Help Center
              </a>
              <span>|</span>
              <a href="#" style={{ color: colors.textSecondary }}>
                Privacy
              </a>
              <span>|</span>
              <a href="#" style={{ color: colors.textSecondary }}>
                Terms of Service
              </a>
            </div>
            <p className="text-xs" style={{ color: colors.textSecondary }}>
              Copyright © {new Date().getFullYear()}. All rights reserved.
            </p>
          </div>
        </div>
      </div>

      {/* <div className="absolute bottom-6 left-0 right-0 z-10 text-center space-y-2">
        <div className="flex items-center justify-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
          <a href="#" style={{ color: colors.textSecondary }}>Help Center</a>
          <span>|</span>
          <a href="#" style={{ color: colors.textSecondary }}>Privacy</a>
          <span>|</span>
          <a href="#" style={{ color: colors.textSecondary }}>Terms of Service</a>
        </div>
        <p className="text-xs" style={{ color: colors.textSecondary }}>
          Copyright © {new Date().getFullYear()}. All rights reserved.
        </p>
      </div> */}
    </div>
  );
}

export default Login;
